import { useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';

export function useNativeShare() {
  const isNative = Capacitor.isNativePlatform();

  const shareImage = useCallback(async (canvas, { title = 'trainedbyRS', text = '', fileName = 'trainedbyrs.png', successMessage, errorMessage } = {}) => {
    try {
      if (isNative) {
        const dataUrl = canvas.toDataURL('image/png');
        const { Share } = await import('@capacitor/share');
        await Share.share({ title, text: text || title, url: dataUrl, dialogTitle: title });
        return 'shared';
      }

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], fileName, { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title, text: text || 'Check it out on trainedbyRS!', url: 'https://trainedbyrs.com' });
        return 'shared';
      }

      // Fallback: download
      const link = document.createElement('a');
      link.download = fileName;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success(successMessage || 'Image downloaded — share it to your story!');
      return 'downloaded';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
      console.error('Share failed:', err);
      toast.error(errorMessage || 'Failed to share image');
      return 'error';
    }
  }, [isNative]);

  const downloadImage = useCallback((canvas, fileName = 'trainedbyrs.png') => {
    try {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download image');
    }
  }, []);

  return { shareImage, downloadImage, isNative };
}
